import NeurodivergentCoachTag from "@/Components/ClientCoachInfo/NeurodivergentCoachTag";
import { Industry, IndustryPivotData } from "@/types/industry";
import { IndustryJob, IndustryJobPivotData } from "@/types/industryJob";
import { hasNeurodivergentMatch } from "@/utils/hasNeurodivergenceMatch";
import { calculateLifeAreaOverlap } from "@/utils/lifeAreaOverlap";
import classNames from "classnames";
import { CoachInfoSubcomponentProps } from "../CoachInfo";

export default function Highlights({
    coachInfoData,
}: CoachInfoSubcomponentProps) {
    const hasIndustryMatch =
        getRoleTag(
            coachInfoData?.client?.attributes?.industry_job ?? null,
            coachInfoData?.coach.industry_jobs ?? [],
        ) !== null ||
        getIndustryTag(
            coachInfoData?.client?.attributes?.industry ?? null,
            coachInfoData?.coach.industries ?? [],
        ) !== null;

    const lifeAreaOverlap = coachInfoData
        ? calculateLifeAreaOverlap(
              coachInfoData.coach.life_areas,
              coachInfoData.client?.life_areas ?? [],
              {
                  limit: 3,
                  quizType: coachInfoData.client?.attributes?.quiz_type,
              },
          )
        : [];

    const industryTag = getIndustryTag(
        coachInfoData?.client?.attributes?.industry ?? null,
        coachInfoData?.coach.industries ?? [],
    );
    const roleTag = getRoleTag(
        coachInfoData?.client?.attributes?.industry_job ?? null,
        coachInfoData?.coach.industry_jobs ?? [],
    );

    return !coachInfoData ? (
        <HighlightsSkeleton />
    ) : (
        (lifeAreaOverlap.length > 0 ||
            (coachInfoData?.client &&
                hasNeurodivergentMatch(
                    coachInfoData?.client,
                    coachInfoData.coach,
                )) ||
            hasIndustryMatch) && (
            <div>
                <div className="font-bold text-xl">Highlights</div>
                <div className="flex flex-wrap gap-x-2 gap-y-2 self-stretch p-2">
                    {/* <IndustryTags coach={coach} /> */}
                    {industryTag && (
                        <div
                            key={industryTag}
                            className="w-fit rounded-full bg-brand-pinkDecal/65 px-3 py-1 text-xs font-medium uppercase text-brand-black text-center"
                        >
                            {industryTag}
                        </div>
                    )}
                    {roleTag && (
                        <div
                            key={roleTag}
                            className="w-fit rounded-full bg-brand-pinkDecal/65 px-3 py-1 text-xs font-medium uppercase text-brand-black text-center"
                        >
                            {roleTag}
                        </div>
                    )}
                    {coachInfoData?.client && (
                        <NeurodivergentCoachTag
                            client={coachInfoData.client}
                            coach={coachInfoData.coach}
                        />
                    )}
                    {lifeAreaOverlap.map((area) => (
                        <div
                            key={area.code_name}
                            className={classNames(
                                "flex w-fit rounded-full px-3 py-1 text-xs font-medium uppercase text-brand-black",
                                area.score === 2
                                    ? "bg-brand-pinkDecal/65"
                                    : "bg-brand-lightRed",
                            )}
                        >
                            {area.expertise_tag}
                        </div>
                    ))}
                </div>
            </div>
        )
    );
}

function HighlightsSkeleton() {
    return (
        <div>
            <div className="font-bold text-xl text-brand-gray animate-pulse">
                Highlights
            </div>
            <div className="flex flex-wrap gap-x-2 gap-y-2 self-stretch py-2">
                {Array.from({ length: 6 }).map((_, index) => (
                    <div
                        key={index}
                        className="h-7 w-40 rounded-full bg-brand-lightGray animate-pulse"
                        style={{
                            // Pseudo random width between 60 and 200. Uses prime numbers to avoid patterns.
                            width: ((index * 5867 + 37) % 140) + 60,
                        }}
                    />
                ))}
            </div>
        </div>
    );
}

function getIndustryTag(
    clientIndustry: Industry | null,
    coachIndustries: (Industry & IndustryPivotData)[],
) {
    if (!clientIndustry) return null;

    const industryMatch = coachIndustries.find(
        (ind) => ind.id === clientIndustry.id,
    );

    if (!industryMatch) return null;

    if (industryMatch.score_worked === 2) {
        return `5+ YEARS IN ${clientIndustry.pretty_name.toUpperCase()}`;
    } else if (industryMatch.score_worked === 1) {
        return `EXPERIENCE IN ${clientIndustry.pretty_name.toUpperCase()}`;
    } else if (industryMatch.score_coached === 1) {
        return `COACHED CLIENTS IN ${clientIndustry.pretty_name.toUpperCase()}`;
    }

    return null;
}

function getRoleTag(
    clientIndustryJob: IndustryJob | null,
    coachIndustryJobs: (IndustryJob & IndustryJobPivotData)[],
) {
    if (
        !clientIndustryJob ||
        clientIndustryJob.code_name === "individual_contributor" ||
        clientIndustryJob.code_name === "other"
    ) {
        return null;
    }

    const roleMatch = coachIndustryJobs.find(
        (j) => j.id === clientIndustryJob.id,
    );

    if (!roleMatch) return null;

    if (roleMatch.score_worked === 2) {
        return `5+ YEARS AS ${clientIndustryJob.pretty_name.toUpperCase()}`;
    } else if (roleMatch.score_worked === 1) {
        return `EXPERIENCE AS ${clientIndustryJob.pretty_name.toUpperCase()}`;
    } else if (roleMatch.score_coached === 1) {
        // Pluralize the role (some roles look like "Founder / Entrepreneur", others just "People Manager")
        const pluralRole = clientIndustryJob.pretty_name
            .split(" / ")
            .map((word) => word + "s")
            .join(" / ");
        return `COACHED ${pluralRole.toUpperCase()}`;
    }

    return null;
}
